import { MutationHandlingServiceV2 } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { combineLatest, of, Subject, timer } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { AutoLotView } from 'src/app/const';
import {
  BidUpdateGQL,
  UserLotQuery,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import { NegotiationsService } from 'src/app/services/negotiations.service';
import {
  getLotBiddingDeadline,
  isLotTradingClosed,
  NEGOTIATIONS_OVERSTEP,
} from 'src/app/utils/lot-trading.util';

@Component({
  selector: 'app-lot-negotiations-button',
  templateUrl: './lot-negotiations-button.component.html',
  styleUrls: ['./lot-negotiations-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotNegotiationsButtonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lot: AutoLotView;
  @Input() currentUserLastBid?: UserLotQuery['lot']['currentUserLastBid'];
  @Input() isOtherCountriesUser: boolean;
  @Input() negotiationsPriceStr?: string;
  @Output() actionComplete = new EventEmitter<void>();

  readonly MAX_AMOUNT = 5_900_000;
  @ViewChild('content1') content1: TemplateRef<HTMLElement>;
  @ViewChild('content2') content2: TemplateRef<HTMLElement>;

  form: FormGroup;
  withOverstep = false;
  negotiationsPrice: number;

  tradingClosed$ = of(false);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private ms: NzModalService,
    private translate: TranslateService,
    private mhs2: MutationHandlingServiceV2,
    private bidUpdateGQL: BidUpdateGQL,
    private negotiationsService: NegotiationsService,
  ) {}

  get tradingClosed(): boolean {
    return !this.lot || isLotTradingClosed(this.lot);
  }

  get deadline(): number {
    return this.lot ? getLotBiddingDeadline(this.lot).toMillis() : null;
  }

  get confirmationText(): string {
    return this.translate.instant('NEGOTIATIONS_CONFIRM_1', {
      link: `<a href="/article/negotiations" target="_blank">${this.translate.instant(
        'NEGOTIATIONS_WORK',
      )}</a>`,
    });
  }

  ngOnInit(): void {
    this.initTradingClosed();
  }

  ngOnChanges(): void {
    const currentAmount = this.currentUserLastBid?.amount || 0;
    const minimum = Number(this.negotiationsPriceStr) + NEGOTIATIONS_OVERSTEP;
    this.withOverstep = currentAmount < minimum;
    this.negotiationsPrice = Math.max(currentAmount, minimum);

    if (!this.form) {
      this.form = this.fb.group({ amount: [null] });
    }
    this.form.controls.amount.setValidators([
      Validators.required,
      Validators.min(this.negotiationsPrice),
      ...(!this.isOtherCountriesUser ? [Validators.max(this.MAX_AMOUNT)] : []),
    ]);
    this.form.controls.amount.updateValueAndValidity();
  }

  openParticipationConfirmation(): void {
    if (!this.currentUserLastBid) {
      return;
    }

    const ref = this.ms.confirm({
      nzContent: this.content1,
      nzOkDisabled: this.tradingClosed,
      nzOnOk: () => {
        if (this.tradingClosed) {
          return false;
        }
        this.openParticipationBidForm();
      },
    });

    this.tradingClosed$
      .pipe(takeUntil(ref.afterClose), takeUntil(this.destroy$))
      .subscribe((closed) => ref.updateConfig({ nzOkDisabled: closed }));
  }

  openParticipationBidForm(): void {
    if (!this.currentUserLastBid) {
      return;
    }

    const ref: NzModalRef = this.ms.confirm(
      {
        nzContent: this.content2,
        nzOkDisabled: this.form.invalid || this.tradingClosed,
        nzOnOk: async () => {
          if (this.form.invalid || this.tradingClosed) {
            return false;
          }

          const result = await this.mhs2
            .mutate(this.bidUpdateGQL, {
              input: {
                id: this.currentUserLastBid.id,
                isNegotiationsAccepted: true,
                newAmount: this.form.value.amount,
              },
            })
            .data.pipe(take(1), takeUntil(this.destroy$))
            .toPromise();

          if (!result?.bidUpdate || result.bidUpdate.error) {
            return false;
          }
          this.negotiationsService.refresh();
          this.form.reset();
          this.actionComplete.emit();
        },
      },
      'warning',
    );

    combineLatest([this.tradingClosed$, this.form.statusChanges.pipe(startWith(this.form.status))])
      .pipe(
        map(([closed]) => closed || this.form.invalid),
        distinctUntilChanged(),
        takeUntil(ref.afterClose),
        takeUntil(this.destroy$),
      )
      .subscribe((disabled) => ref.updateConfig({ nzOkDisabled: disabled }));
  }

  private initTradingClosed(): void {
    this.tradingClosed$ = timer(0, 1000).pipe(
      map(() => this.tradingClosed),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
